import { Component, Input, OnChanges } from '@angular/core';
import { OrderProtectionExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ViewMode } from '../../../../models';

const SAMPLE_SUBTOTAL = 60;

const SIZE_PX: Record<string, string> = {
  extraSmall: '11px',
  small: '12px',
  base: '14px',
  large: '16px',
  extraLarge: '20px',
};

const BORDER_WIDTH_PX: Record<string, string> = { base: '1px', medium: '2px', thick: '3px' };
const BORDER_RADIUS_PX: Record<string, string> = { small: '4px', base: '8px', large: '16px' };
const BACKGROUND: Record<string, string> = {
  transparent: 'transparent',
  base: 'var(--surface-default)',
  subdued: 'var(--surface-dim)',
};

@Component({
  selector: 'order-protection-example',
  templateUrl: './order-protection-example.component.html',
  styleUrls: ['./order-protection-example.component.scss'],
  standalone: false,
})
export class OrderProtectionExampleComponent implements OnChanges {
  @Input() template: Partial<OrderProtectionExtension.Template>;
  @Input() mode: ViewMode = null;
  @Input() isMobile = false;

  titleText = '';
  subtitleText = '';
  disclaimerText = '';
  priceLabel = '';
  showDisclaimer = false;
  showLogo = true;
  showInfoModal = true;
  isCheckbox = false;
  modalOpen = false;
  readonly coverageItems = [
    'Theft',
    'Damaged items',
    'Missing items',
    'Lost packages',
    'Wrong items',
    'Returned to sender',
  ];
  containerStyle: Record<string, string> = {};
  titleStyle: Record<string, string> = {};
  subtitleStyle: Record<string, string> = {};
  disclaimerStyle: Record<string, string> = {};

  ngOnChanges() {
    const config: Partial<OrderProtectionExtension.WidgetConfig> = this.template?.widget?.config ?? {};
    this.titleText = config.titleText || 'Protect your order';
    this.subtitleText = config.infoText || '';
    this.disclaimerText = config.disclaimerTextOn || '';
    this.showDisclaimer = !!config.showDisclaimerText && !!this.disclaimerText;
    this.showLogo = !config.hideLogo;
    this.showInfoModal = config.showInfoModal ?? true;
    this.isCheckbox = config.actionButtonType === OrderProtectionExtension.ActionType.CHECKBOX;
    this.priceLabel = this.buildPriceLabel();

    this.titleStyle = {
      'font-size': SIZE_PX[config.titleTextSize ?? 'base'] ?? SIZE_PX['base'],
      'font-weight': config.titleTextBold ? '700' : '400',
      color: 'var(--on-surface-default)',
    };
    this.subtitleStyle = {
      'font-size': SIZE_PX[config.infoTextSize ?? 'base'] ?? SIZE_PX['base'],
      color: 'var(--on-surface-light)',
    };
    this.disclaimerStyle = {
      'font-size': SIZE_PX[config.disclaimerTextOnSize ?? 'small'] ?? SIZE_PX['small'],
      color: 'var(--on-surface-light)',
    };
    this.containerStyle = {
      background: BACKGROUND[config.backgroundColor ?? 'transparent'] ?? BACKGROUND['transparent'],
      'border-radius': BORDER_RADIUS_PX[config.borderRadius ?? 'base'] ?? BORDER_RADIUS_PX['base'],
      border: config.showBorder
        ? `${BORDER_WIDTH_PX[config.borderWidth ?? 'base'] ?? BORDER_WIDTH_PX['base']} ${config.borderStyle ?? 'solid'} var(--on-surface-light)`
        : '1px solid transparent',
    };
  }

  openInfoModal() {
    this.modalOpen = true;
  }

  closeInfoModal() {
    this.modalOpen = false;
  }

  private buildPriceLabel(): string {
    const pricing = this.template?.pricing;
    const rate = Number(pricing?.rate ?? 0);
    const amount =
      pricing?.rateType === OrderProtectionExtension.RateType.PERCENTAGE ? (rate / 100) * SAMPLE_SUBTOTAL : rate;
    return `$${amount.toFixed(2)}`;
  }
}
